/** Small Marketplace building blocks: badges, office dots, chips, the dunning banner. */
import { Badge, EmptyState, StatusChip, officeColor, type Tone } from '@velora/ui';
import type { CatalogModule, ModuleBadge, StoreOffice } from '@velora/contracts/marketplace';
import { AlertTriangle, Monitor, RefreshCw, ShieldCheck, Sparkles, Store } from 'lucide-react';
import type { ReactNode } from 'react';
import { BILLING_UNAVAILABLE_COPY, isBillingUnavailable } from '../api.js';
import {
  COMPED_LABEL,
  PLACEHOLDER_PRICE_LABEL,
  PLACEHOLDER_PRICE_TITLE,
  type ChipView,
} from '../model.js';

const BADGE: Record<ModuleBadge, { label: string; tone: Tone; icon?: ReactNode }> = {
  popular: { label: 'Popular', tone: 'neutral' },
  new: { label: 'New', tone: 'info' },
  ai: { label: 'AI', tone: 'brand', icon: <Sparkles size={11} /> },
  writeback: { label: 'Write-back', tone: 'neutral', icon: <RefreshCw size={11} /> },
  desktop: { label: 'Desktop app', tone: 'neutral', icon: <Monitor size={11} /> },
  beta: { label: 'Beta', tone: 'warning' },
};

export function ModuleBadges({ module, baa = false }: { module: CatalogModule; baa?: boolean }) {
  if (module.badges.length === 0 && !baa) return null;
  return (
    <span className="mk-badges">
      {module.badges.map((b) => (
        <Badge
          key={b}
          tone={BADGE[b].tone}
          variant={b === 'popular' || b === 'writeback' || b === 'desktop' ? 'outline' : 'soft'}
          {...(BADGE[b].icon ? { icon: BADGE[b].icon } : {})}
        >
          {BADGE[b].label}
        </Badge>
      ))}
      {baa ? (
        <Badge tone="neutral" variant="outline" icon={<ShieldCheck size={11} />}>
          Covered by your BAA
        </Badge>
      ) : null}
    </span>
  );
}

export function Chip({ chip }: { chip: ChipView | null }) {
  if (!chip) return null;
  return <StatusChip tone={chip.tone} label={chip.label} />;
}

export function OfficeDot({ office, index }: { office: StoreOffice; index: number }) {
  return (
    <span
      className="mk-dot"
      style={{
        background: officeColor(
          { id: office.id, name: office.name, colorIndex: office.colorIndex },
          index,
        ),
      }}
      aria-hidden
    />
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="mk-kbd">{children}</kbd>;
}

/** Admin dunning banner (marketplace.md 4.9). */
export function DunningBanner({
  graceEnds,
  card,
  onUpdateCard,
}: {
  graceEnds: string;
  card: string | null;
  onUpdateCard?: () => void;
}) {
  return (
    <div className="mk-dunning" role="alert">
      <AlertTriangle size={16} aria-hidden />
      <div className="mk-dunning__text">
        <b>Payment failed{card ? ` on the card ending ${card}` : ''}.</b> Update the card before{' '}
        {graceEnds} to keep every module running. After that, affected modules turn read-only.
      </div>
      {onUpdateCard ? (
        <button type="button" className="mk-dunning__btn" onClick={onUpdateCard}>
          Update card
        </button>
      ) : null}
    </div>
  );
}

export function SimulatorTag() {
  return (
    <span
      className="mk-sim"
      title="Local development: Square is simulated in the API. No real charges."
    >
      Square simulator
    </span>
  );
}

/** Billing is not live (beta hold): nothing here is owed to Velora. */
export function CompedTag() {
  return (
    <span
      className="mk-sim mk-comped"
      data-comped=""
      title="Add-on billing is not live. Modules are comped during the beta and nothing is invoiced."
    >
      {COMPED_LABEL}
    </span>
  );
}

/** The list price is not set yet (`pricing.placeholder`): shown beside catalog prices. */
export function PlaceholderPriceTag({ module: m }: { module: CatalogModule }) {
  if (!m.pricing.placeholder) return null;
  return (
    <span
      className="mk-sim mk-placeholder"
      data-placeholder-price=""
      title={PLACEHOLDER_PRICE_TITLE}
    >
      {PLACEHOLDER_PRICE_LABEL}
    </span>
  );
}

/** Why buy, trial, turn-on and invoice actions are greyed (ADM-11). */
export const BILLING_OFF_REASON =
  'Billing not enabled: add-on billing (Square) is not set up on this server, so modules cannot be bought, trialled or changed here.';

/**
 * Banner for the read-only Marketplace when the server has no Square setup:
 * the catalog, what is on at each office and staff requests are real; only
 * buying, trials and invoices are unavailable.
 */
export function BillingOffNotice() {
  return (
    <p className="mk-callout" data-tone="neutral" data-billing-off="" role="note">
      <b>Billing not enabled.</b> You can see every module, what is on at each office and staff
      requests. Buying, trials, card and invoices are unavailable until Velora support sets up
      add-on billing. Modules already on keep working.
    </p>
  );
}

/**
 * A Marketplace page whose data did not load: the "not set up yet" state
 * when the server has no billing configured, otherwise a load error.
 */
export function MarketplaceLoadError({ error, title }: { error: unknown; title: string }) {
  const unavailable = isBillingUnavailable(error);
  return (
    <div className="shell-page" data-billing-unavailable={unavailable ? '' : undefined}>
      <EmptyState
        icon={<Store />}
        title={unavailable ? 'Marketplace add-ons are not set up yet' : title}
        description={unavailable ? BILLING_UNAVAILABLE_COPY : 'Check the connection and try again.'}
      />
    </div>
  );
}
